import Loading from "@components/loading/Loading";
import {
  Button,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  useCreateBookingItemCost,
  useCreateQuoteItem,
  useDeleteBookingItemCost,
  useUpdateBookingItemCost,
  useUpdateQuoteItem,
} from "@store/actions/quoteActions";
import { BookingItemCostType } from "@store/operations/bookingOperations";
import { useBoatLowestPrice } from "components-library";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePreviousDistinct } from "react-use";
import { move } from "utils/arrays";
import NewQuoteItemCost from "./NewQuoteItemCost";
import QuoteItemCost from "./QuoteItemCost";

function costInput(cost) {
  const input = {
    description: cost.description,
    cost: cost.cost,
    commision: cost.commision,
    type: cost.type,
  };
  for (const field of ["dropOffWharf", "pickUpWharf", "package"]) {
    if (cost[field]?.id) input[field] = { id: cost[field].id };
  }
  return input;
}

function sameCost(left, right) {
  return ["description", "cost", "commision", "type"].every((field) => `${left?.[field] ?? ""}` === `${right?.[field] ?? ""}`)
    && ["dropOffWharf", "pickUpWharf", "package"].every((field) => left?.[field]?.id === right?.[field]?.id);
}

function QuoteItem({
  quote,
  booking,
  item,
  setItem,
  onRemove,
  checkSetValid,
  readOnly,
  onSaveStateChange,
}) {
  const [recomended, setRecomended] = useState(0);
  const { getRate } = useBoatLowestPrice();
  const [quoteItem, setQuoteItem] = useState(item);
  const prevQuoteItem = usePreviousDistinct(quoteItem);
  const [addQuoteItem] = useCreateQuoteItem(quote);
  const [updateQuoteItem] = useUpdateQuoteItem(quote);
  const [updateItemCost] = useUpdateBookingItemCost();
  const [deleteItemCost] = useDeleteBookingItemCost(quote, item);
  const [addItemCost] = useCreateBookingItemCost(quote, item);
  const [open, setOpen] = useState(false);
  const [sortedCosts, setSortedCosts] = useState([]);
  const saveQueue = useRef(Promise.resolve());
  const pendingSaves = useRef(0);
  const failedSaves = useRef(new Map());
  const savedCosts = useRef(new Map());
  const queuedCosts = useRef(new Map());
  const mounted = useRef(true);
  const [saveState, setSaveState] = useState({ pending: 0, error: false });
  const latestItem = useRef(item);
  latestItem.current = item;
  for (const cost of item.costs || []) {
    if (cost.id && !savedCosts.current.has(cost.id)) savedCosts.current.set(cost.id, cost);
  }
  const saveKey = item.id || item.bookingItem.id;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; onSaveStateChange?.(saveKey, null); };
  }, [onSaveStateChange, saveKey]);
  const queueSave = (key, save, retryable = true) => {
    pendingSaves.current += 1;
    const publish = () => {
      if (mounted.current) {
        const state = { pending: pendingSaves.current, error: failedSaves.current.size > 0 };
        setSaveState(state);
        onSaveStateChange?.(saveKey, state);
      }
    };
    publish();
    const run = saveQueue.current.then(save);
    saveQueue.current = run.catch(() => {});
    run.then(() => failedSaves.current.delete(key), () => failedSaves.current.set(key, { save, retryable }))
      .finally(() => { pendingSaves.current -= 1; publish(); });
  };
  const setCost = useCallback(
    (v) => {
      const costs = (item.costs || []).map((c) => (c.id === v.id ? v : c));
      setItem({
        ...item,
        costs,
      });
    },
    [item]
  );

  const isAdditional = useMemo(() => quote.type === "additional");

  const addWharfs = () => {
    let costs = item.costs || [];
    const dropOffIdx = costs
      .map((c) => c.type)
      .indexOf(BookingItemCostType.dropOff);
    const pickupIdx = costs
      .map((c) => c.type)
      .indexOf(BookingItemCostType.pickUp);
    if (dropOffIdx === -1) {
      costs = [{ type: BookingItemCostType.dropOff }, ...costs];
    }
    if (pickupIdx === -1) {
      costs = [{ type: BookingItemCostType.pickUp }, ...costs];
    }
    return costs;
  };

  useEffect(() => {
    if (!quoteItem) {
      return;
    }

    if (booking.cruiseDate && booking.hours && quoteItem.bookingItem.boat) {
      const baseCost = getRate(
        booking.cruiseDate,
        quoteItem.bookingItem.boat,
        booking.hours
      );
      setRecomended(baseCost);
    }

    async function saveCosts() {
      let costs = quoteItem.costs || [];
      let itemId = quoteItem.id;

      // we do not update quote item is read only as this means a transaction has happened
      if (!readOnly) {
        const pickUpIds = costs
          .filter((c) => c.pickUpWharf?.id)
          .map((c) => c.pickUpWharf.id)
          .slice()
          .sort();
        const prevPickUpIds = (prevQuoteItem?.costs || [])
          .filter((c) => c.pickUpWharf?.id)
          .map((c) => c.pickUpWharf.id)
          .slice()
          .sort();
        if (
          !itemId || (prevQuoteItem && (
            Number(quoteItem.baseCost) !== Number(prevQuoteItem.baseCost) ||
            Number(quoteItem.commision) !== Number(prevQuoteItem.commision) ||
            pickUpIds.length !== prevPickUpIds.length ||
            !pickUpIds.every((value, index) => value === prevPickUpIds[index])
          ))
        ) {
          const input = {
            baseCost: quoteItem.baseCost,
            commision: quoteItem.commision,
            bookingItem: { id: quoteItem.bookingItem.id },
          };
          if (itemId) {
            await updateQuoteItem({
              variables: {
                id: itemId,
                input,
              },
            });
          } else {
            const { data } = await addQuoteItem({
              variables: {
                quoteID: quote.id,
                input,
              },
            });
            itemId = data.createQuoteItem.id;
            // if creating and not additional then add dropoff and pick
            if (!isAdditional) {
              costs = addWharfs();
            }
          }
        }
      }

      if (!itemId) {
        return;
      }

      // Existing charges save independently on blur, so a later edit cannot
      // replay an older value from a sibling charge.
      const promises = costs.filter((cost) => !cost.id);

      for (let i = 0; i < promises.length; i++) {
        const cost = promises[i];
        await addItemCost({
          variables: {
            quoteItemID: itemId,
            input: costInput(cost),
          },
        });
      }
    }

    queueSave("item", saveCosts, Boolean(quoteItem.id) && (quoteItem.costs || []).every((cost) => cost.id));
  }, [quoteItem]);

  const onDeleteCost = useCallback((id) => {
    deleteItemCost({
      variables: {
        id,
      },
    });
    setTimeout(checkSetValid, 1);
  }, []);

  const onBlur = useCallback(() => {
    setQuoteItem(item);
  }, [item]);

  const onCostBlur = (cost) => {
    if (readOnly || !cost.id) return;
    const previousInput = queuedCosts.current.get(cost.id) || savedCosts.current.get(cost.id);
    if (sameCost(previousInput, cost) && !failedSaves.current.has(cost.id)) return;
    queuedCosts.current.set(cost.id, cost);
    queueSave(cost.id, async () => {
      const previous = savedCosts.current.get(cost.id);
      await updateItemCost({ variables: { id: cost.id, input: costInput(cost) } });
      // Preserve the existing pickup-tax recalculation without resaving the
      // other charges from an earlier item snapshot.
      if (previous?.pickUpWharf?.id !== cost.pickUpWharf?.id) {
        const current = latestItem.current;
        await updateQuoteItem({ variables: { id: current.id, input: {
          baseCost: current.baseCost, commision: current.commision,
          bookingItem: { id: current.bookingItem.id },
        } } });
      }
      savedCosts.current.set(cost.id, cost);
      if (queuedCosts.current.get(cost.id) === cost) queuedCosts.current.delete(cost.id);
    });
  };

  useEffect(() => {
    let costs = [...(item.costs || [])]
      .map((c) => c)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const typeMap = costs.map((c) => c.type);
    const dropOffIdx = typeMap.indexOf(BookingItemCostType.dropOff);
    const pickupIdx = typeMap.indexOf(BookingItemCostType.pickUp);

    if (dropOffIdx >= 0) {
      setTimeout(() => {
        costs = move(costs, dropOffIdx, 0);
      }, 0);
    }
    if (pickupIdx >= 0) {
      costs = move(costs, pickupIdx, 0);
    }
    setSortedCosts(costs);
    setTimeout(checkSetValid, 1);
  }, [item]);

  if (!item.id) {
    return <Loading />;
  }

  return (
    <Grid item xs={12} container spacing={2} alignItems="center">
      <Grid item xs={12} container justify="space-between">
        <Typography variant="h6" gutterBottom>
          {item.bookingItem?.boat?.name}
        </Typography>
        <Button
          color="primary"
          variant="outlined"
          onClick={onRemove}
          disabled={!!readOnly}
        >
          Remove
        </Button>
      </Grid>
      <Grid item xs={12}>
        {/* <br /> */}
        <Divider light />
        <br />
      </Grid>

      {!isAdditional && (
        <>
          <Grid item xs={6}>
            <Typography variant="caption" display="inline">
              Rate for{" "}
            </Typography>
            <Typography variant="body1" display="inline" color="primary">
              {booking.hours} hrs{" "}
            </Typography>
            <Typography variant="caption" display="inline">
              for{" "}
            </Typography>
            <Typography variant="body1" display="inline" color="primary">
              {booking.groupSize} people
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Rental Cost"
              variant="outlined"
              disabled={!!readOnly}
              helperText={`Recomended $${recomended} ( ${item.commision || 0}% commision)`}
              size="small"
              type="number"
              fullWidth
              value={item.baseCost ?? ""}
              onChange={(e) =>
                setItem({
                  ...item,
                  baseCost: e.target.value,
                })
              }
              onBlur={onBlur}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
        </>
      )}

      {sortedCosts.map((cost, idx) => (
        <QuoteItemCost
          boat={item.bookingItem?.boat}
          onDelete={onDeleteCost}
          cost={cost}
          setCost={setCost}
          key={cost.id || idx}
          readOnly={readOnly}
          onBlur={onCostBlur}
        />
      ))}
      <Grid item xs={12}>
        {saveState.pending > 0 && <Typography variant="body2" color="textSecondary">Saving quote changes...</Typography>}
        {saveState.error && <>
          <Typography variant="body2" color="error">Some quote changes could not be saved.</Typography>
          {[...failedSaves.current.values()].some((failure) => !failure.retryable)
            ? <Typography variant="body2">Reload the quote to check its saved details before continuing.</Typography>
            : <Button disabled={saveState.pending > 0} onClick={() => {
              for (const [key, failure] of failedSaves.current) queueSave(key, failure.save);
            }}>Retry saving</Button>}
        </>}
        <Button
          variant="outlined"
          disabled={!!readOnly}
          color="primary"
          size="small"
          onClick={() => setOpen(true)}
        >
          Add additional item
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <NewQuoteItemCost
        quote={quote}
        open={open}
        setOpen={setOpen}
        onSaved={() => setOpen(false)}
        booking={booking}
        item={item}
      />
    </Grid>
  );
}

export default memo(QuoteItem);
